import { createContext } from 'preact';
import { useState, useContext, useEffect } from 'preact/hooks';
import { wishlistService, WishlistResponse } from '../services/wishlistService';

interface WishlistContextType {
    wishlistItems: Set<string>;
    isInWishlist: (postId: string | number) => boolean;
    addToWishlist: (postId: string | number) => Promise<WishlistResponse>;
    removeFromWishlist: (postId: string | number) => Promise<WishlistResponse>;
    toggleWishlist: (postId: string | number) => Promise<WishlistResponse>;
    wishlistCount: number;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export function WishlistProvider({ children }: { children: any }) {
    const [wishlistItems, setWishlistItems] = useState<Set<string>>(new Set());
    const [wishlistCount, setWishlistCount] = useState<number>(0);

    // Load initial wishlist items
    useEffect(() => {
        const loadInitialItems = async () => {
            try {
                const response = await wishlistService.getItems();

                if (response.success && response.data?.items) {
                    setWishlistItems(new Set(response.data.items));
                    setWishlistCount(response.data.items_count || response.data.items.length);
                } else {
                    setWishlistItems(new Set());
                    setWishlistCount(0);
                }
            } catch (error) {
                console.error('Error loading initial wishlist:', error);
                // If there's an error loading, just start with empty wishlist
                setWishlistItems(new Set());
                setWishlistCount(0);
            }
        };

        loadInitialItems();
    }, []);

    // Function to notify WordPress components about wishlist changes
    const notifyWishlistChange = (postId: string | number, action: 'add' | 'remove', count: number, updatedItems: Set<string>) => {
        // Create a custom event that WordPress components can listen to
        const event = new CustomEvent('wishlistChanged', {
            detail: {
                postId,
                action,
                count,
                items: Array.from(updatedItems)
            }
        });
        window.dispatchEvent(event);

        // Also trigger any existing WordPress wishlist handlers
        const wordpressLinks = document.querySelectorAll(`[data-pid="${postId}"]`);
        wordpressLinks.forEach(link => {
            if (action === 'add') {
                link.classList.add('active');
                link.classList.add('jump');
            } else {
                link.classList.remove('active');
                link.classList.remove('jump');
            }
        });
    };

    const isInWishlist = (postId: string | number): boolean => {
        return wishlistItems.has(postId.toString());
    };

    const addToWishlist = async (postId: string | number): Promise<WishlistResponse> => {
        try {
            const response = await wishlistService.addItem(postId.toString());
            if (response.success) {
                const updatedItems = new Set(wishlistItems).add(postId.toString());
                setWishlistItems(updatedItems);
                const newCount = response.data?.items_count ?? 0;
                setWishlistCount(newCount);

                // Notify WordPress components with updated items set
                notifyWishlistChange(postId, 'add', newCount, updatedItems);
            }
            return response;
        } catch (error) {
            console.error('Error adding to wishlist:', error);
            throw error;
        }
    };

    const removeFromWishlist = async (postId: string | number): Promise<WishlistResponse> => {
        try {
            const response = await wishlistService.removeItem(postId.toString());
            if (response.success) {
                const updatedItems = new Set(wishlistItems);
                updatedItems.delete(postId.toString());
                setWishlistItems(updatedItems);
                const newCount = response.data?.items_count ?? 0;
                setWishlistCount(newCount);

                // Notify WordPress components with updated items set
                notifyWishlistChange(postId, 'remove', newCount, updatedItems);
            }
            return response;
        } catch (error) {
            console.error('Error removing from wishlist:', error);
            throw error;
        }
    };

    const toggleWishlist = async (postId: string | number): Promise<WishlistResponse> => {
        if (isInWishlist(postId)) {
            return removeFromWishlist(postId.toString());
        } else {
            return addToWishlist(postId.toString());
        }
    };

    return (
        <WishlistContext.Provider value={{
            wishlistItems,
            isInWishlist,
            addToWishlist,
            removeFromWishlist,
            toggleWishlist,
            wishlistCount
        }}>
            {children}
        </WishlistContext.Provider>
    );
}

export function useWishlist(): WishlistContextType {
    const context = useContext(WishlistContext);
    if (context === undefined) {
        throw new Error('useWishlist must be used within a WishlistProvider');
    }
    return context;
}